import React from 'react';
import {
  X,
  ShieldCheck,
  Zap,
  ArrowRight,
  ExternalLink,
  Info,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Smartphone,
  CreditCard,
  Banknote,
} from 'lucide-react';
import { CanonicalTransferQuote } from '../../types';
import { financialFormat, formatRateDisplay } from '../../services/fxEngine';
import { transferEngine } from '../../services/transferEngine';

interface TransferDetailDrawerProps {
  quote: CanonicalTransferQuote | null;
  isOpen: boolean;
  onClose: () => void;
}

export const TransferDetailDrawer: React.FC<TransferDetailDrawerProps> = ({
  quote,
  isOpen,
  onClose,
}) => {
  if (!isOpen || !quote) return null;

  const provider = transferEngine.getProviderById(quote.providerId);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden flex justify-end bg-black/40 backdrop-blur-xs transition-opacity animate-in fade-in duration-200">
      <div className="w-full max-w-lg bg-white h-full shadow-2xl flex flex-col justify-between overflow-y-auto animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="p-6 border-b border-[#E5E7EB] flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0E7044] text-white flex items-center justify-center font-black text-sm">
              {quote.providerLogoText || quote.providerName.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <h3 className="text-base font-bold text-[#1A1A1A] flex items-center gap-2">
                <span>{quote.providerName}</span>
                {quote.isPromotional && (
                  <span className="text-[10px] font-bold bg-[#DCFCE7] text-[#0E7044] px-2 py-0.5 rounded-full">
                    Promo
                  </span>
                )}
              </h3>
              <p className="text-xs text-[#6B7280]">
                {quote.sendingCountry} ({quote.sendCurrency}) → {quote.receivingCountry} ({quote.receiveCurrency})
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-[#6B7280] hover:text-[#1A1A1A] hover:bg-[#F3F4F6] transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-6 flex-1">
          {/* Main Hero Card */}
          <div className="p-5 bg-[#F0FDF4] border border-[#DCFCE7] rounded-2xl space-y-3">
            <div className="text-xs font-bold uppercase tracking-wider text-[#0E7044] flex items-center justify-between">
              <span>Verified Recipient Payout</span>
              <span className="text-[10px] font-semibold bg-white px-2 py-0.5 rounded-md border border-[#DCFCE7]">
                {quote.quoteType.replace(/_/g, ' ')}
              </span>
            </div>
            <div className="text-3xl font-black text-[#1A1A1A]">
              {financialFormat(quote.recipientAmount, quote.receiveCurrency)}{' '}
              <span className="text-base font-bold text-[#0E7044]">{quote.receiveCurrency}</span>
            </div>
            <div className="text-xs text-[#4B5563] flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-[#0E7044]" />
              <span>Estimated Delivery: <strong>{quote.deliveryEstimateText}</strong></span>
            </div>
          </div>

          {/* Itemized Price & Math Breakdown */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wider text-[#6B7280]">
              Transparent Cost Breakdown
            </h4>

            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl p-4 space-y-3 text-sm">
              <div className="flex justify-between items-center text-[#4B5563]">
                <span>Send Amount (Principal)</span>
                <span className="font-semibold text-[#1A1A1A]">
                  {financialFormat(quote.sendAmount, quote.sendCurrency)} {quote.sendCurrency}
                </span>
              </div>

              <div className="flex justify-between items-center text-[#4B5563]">
                <div className="flex items-center gap-1.5">
                  <span>Explicit Transfer Fee</span>
                  {quote.feeAmount === 0 && (
                    <span className="text-[10px] font-bold text-[#0E7044] bg-[#DCFCE7] px-1.5 py-0.2 rounded">
                      $0 Fee
                    </span>
                  )}
                </div>
                <span className="font-semibold text-[#1A1A1A]">
                  {quote.feeAmount === 0
                    ? '0.00 ' + quote.sendCurrency
                    : `${financialFormat(quote.feeAmount, quote.feeCurrency)} ${quote.feeCurrency}`}
                </span>
              </div>

              <div className="flex justify-between items-center text-[#4B5563]">
                <span>Total Amount Debited</span>
                <span className="font-bold text-[#1A1A1A]">
                  {financialFormat(quote.totalDebit, quote.sendCurrency)} {quote.sendCurrency}
                </span>
              </div>

              <div className="pt-2 border-t border-[#E5E7EB] flex justify-between items-center text-[#4B5563]">
                <span>Amount Converted</span>
                <span className="font-semibold text-[#1A1A1A]">
                  {financialFormat(quote.amountConverted, quote.sendCurrency)} {quote.sendCurrency}
                </span>
              </div>

              <div className="flex justify-between items-center text-[#4B5563]">
                <span>Provider Exchange Rate</span>
                <span className="font-mono font-bold text-[#1A1A1A]">
                  1 {quote.sendCurrency} = {formatRateDisplay(quote.providerExchangeRate)} {quote.receiveCurrency}
                </span>
              </div>

              <div className="flex justify-between items-center text-[#4B5563]">
                <span className="flex items-center gap-1">
                  <span>YamooPay Benchmark FX</span>
                  <Info className="w-3.5 h-3.5 text-[#9CA3AF]" />
                </span>
                <span className="font-mono text-[#0E7044] font-semibold">
                  1 {quote.sendCurrency} = {formatRateDisplay(quote.derivedMetrics.benchmarkRate)} {quote.receiveCurrency}
                </span>
              </div>

              <div className="flex justify-between items-center text-[#4B5563]">
                <span>Exchange-Rate FX Markup</span>
                <span className={`font-semibold ${quote.derivedMetrics.fxMarkupPercentage === 0 ? 'text-[#0E7044]' : 'text-[#D97706]'}`}>
                  {quote.derivedMetrics.fxMarkupPercentage === 0 ? '0.00% (Mid-Market)' : `${quote.derivedMetrics.fxMarkupPercentage.toFixed(2)}%`}
                </span>
              </div>

              <div className="pt-2 border-t border-[#E5E7EB] flex justify-between items-center font-bold text-[#1A1A1A]">
                <span>Estimated Total Cost</span>
                <span className="text-[#0E7044]">
                  {financialFormat(quote.derivedMetrics.estimatedTotalCost, quote.sendCurrency)} {quote.sendCurrency}
                </span>
              </div>
            </div>
          </div>

          {/* Delivery & Payout Details */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl space-y-1">
              <div className="text-[11px] text-[#6B7280] font-semibold uppercase flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-[#0E7044]" />
                <span>Payment Method</span>
              </div>
              <div className="text-xs font-bold text-[#1A1A1A]">
                {quote.paymentMethodLabel}
              </div>
            </div>

            <div className="p-3.5 bg-[#F9FAFB] border border-[#E5E7EB] rounded-xl space-y-1">
              <div className="text-[11px] text-[#6B7280] font-semibold uppercase flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5 text-[#0E7044]" />
                <span>Payout Method</span>
              </div>
              <div className="text-xs font-bold text-[#1A1A1A]">
                {quote.payoutMethodLabel}
              </div>
            </div>
          </div>

          {/* Provenance & Regulatory Metadata */}
          {provider && (
            <div className="p-4 bg-white border border-[#E5E7EB] rounded-xl space-y-2 text-xs">
              <div className="font-bold text-[#1A1A1A] flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-[#0E7044]" />
                <span>Provider Regulatory & Security Profile</span>
              </div>
              <div className="text-[#6B7280] space-y-1">
                <p><strong>Legal Entity:</strong> {provider.legalName}</p>
                <p><strong>Headquarters:</strong> {provider.headquarters} (Est. {provider.yearFounded})</p>
                <p><strong>Regulators:</strong> {provider.regulators.join(' • ')}</p>
                <p><strong>License:</strong> {provider.licenseInfo}</p>
              </div>
            </div>
          )}

          {/* Compliance & Disclosure Note */}
          <div className="p-3.5 bg-[#FEF3C7] border border-[#FDE68A] rounded-xl text-[11px] text-[#92400E] space-y-1">
            <div className="font-bold flex items-center gap-1.5">
              <Info className="w-3.5 h-3.5" />
              <span>Independent Comparison Guarantee</span>
            </div>
            <p>
              YamooPay evaluates recipient amounts and fees objectively. Commercial relationships or affiliate commissions do not influence our organic rankings.
            </p>
          </div>
        </div>

        {/* Action Footer */}
        <div className="p-6 border-t border-[#E5E7EB] bg-white space-y-2">
          <a
            href={quote.deepLinkUrl}
            target="_blank"
            rel="sponsored nofollow noopener noreferrer"
            onClick={() => transferEngine.recordOutboundClick(quote.providerId, `${quote.sendingCountry}_${quote.receivingCountry}`)}
            className="w-full py-3 px-4 bg-[#0E7044] text-white rounded-xl text-sm font-bold flex items-center justify-center gap-2 hover:bg-[#0B5936] transition-colors shadow-sm cursor-pointer"
          >
            <span>Continue to {quote.providerName}</span>
            <ExternalLink className="w-4 h-4" />
          </a>
          <p className="text-[11px] text-center text-[#9CA3AF]">
            You will be redirected securely to the provider’s official website.
          </p>
        </div>
      </div>
    </div>
  );
};
